// The Beta Report form (ACCOUNT_TIER_RULES.md Beta Tester standards, 2026-10-30), from the
// product owner's brief "Language Masters – Beta Feedback Form Implementation Brief".
// Shape and logic only; the wording in four languages is in ./i18n.js.
//
// Question types:
//   rating     excellent | good | fair | poor (product owner, 2026-11-04: four levels;
//              Fair or Poor opens a required comment box, answered as "<id>__why")
//   rating_na  the same, or "na"
//   choice     one of `options`
//   multi      any of `options`
//   yesno      "yes" | "no"
//   text       free text, multiline, up to TEXT_MAX characters
// `required: true` = required whenever shown. `showIf(answers, ctx)` hides a question
// (and skips its validation) unless it returns true; ctx = {language, questionCount}.
// `deps` on a showIf lists the questions it depends on: deleting one of them in Admin
// → Beta Report Wording deletes the follow-up too.
//
// Admin can delete built-in questions and add new ones (2026-11-04). The published
// structure ({deleted: [ids], added: [{id, section, after, type, required, options}]})
// is applied by buildSections(); formSections() is the form as testers see it.

export const SCHEMA_VERSION = 3; // 3: four-level ratings, added questions (2026-11-04)
export const TEXT_MAX = 2000;
export const RATING_VALUES = ["excellent", "good", "fair", "poor"];
export const LOW_RATINGS = ["fair", "poor"];
export const whyId = (id) => `${id}__why`;
export const ADDED_TYPES = ["rating", "choice", "multi", "yesno", "text"];
export const ADDED_ID_RE = /^x_[a-z0-9]{6}$/;

const dep = (fn, deps) => Object.assign(fn, { deps });
const is = (id, value) => dep((a) => a[id] === value, [id]);
const isIn = (id, values) => dep((a) => values.includes(a[id]), [id]);
const nonEnglish = (_a, ctx) => !String(ctx.language || "en").toLowerCase().startsWith("en");
const anyYes = (ids) => dep((a, ctx) => nonEnglish(a, ctx) && ids.some((id) => a[id] === "yes"), ids);

export const SECTIONS = [
  { key: "A", stage: "experience", questions: [
    { id: "a_overall", type: "rating", required: true },
    { id: "a_ease", type: "rating", required: true },
    { id: "a_realistic", type: "rating", required: true },
    { id: "a_useful", type: "rating", required: true },
    { id: "a_use_again", type: "choice", required: true, options: ["yes", "maybe", "no"] },
    { id: "a_recommend", type: "choice", required: true, options: ["yes", "maybe", "no"] },
  ] },
  { key: "B", stage: "experience", questions: [
    { id: "b_worked", type: "yesno", required: true },
    { id: "b_problem_types", type: "multi", required: true, showIf: is("b_worked", "no"), options: [
      "login", "resume_upload", "jd_upload", "setup", "question_generation", "microphone", "interviewer_audio",
      "transcription", "question_display", "repractice", "restart", "end_navigation", "report",
      "pdf_download", "session_counter", "freeze_crash", "error_message", "slow_loading", "other"] },
    { id: "b_describe", type: "text", required: true, showIf: is("b_worked", "no") },
    { id: "b_prevented", type: "choice", required: true, showIf: is("b_worked", "no"), options: ["no", "partially", "yes"] },
    { id: "b_repeated", type: "choice", required: true, showIf: is("b_worked", "no"), options: ["once", "more_than_once", "every_time"] },
  ] },
  { key: "C", stage: "interview", questions: [
    { id: "c_relevant", type: "rating", required: true },
    { id: "c_level", type: "rating", required: true },
    { id: "c_issue", type: "yesno", required: true },
    { id: "c_question_number", type: "question_number", required: false, showIf: is("c_issue", "yes") },
    { id: "c_describe", type: "text", required: true, showIf: is("c_issue", "yes") },
    { id: "c_what_wrong", type: "multi", required: true, showIf: is("c_issue", "yes"), options: [
      "not_relevant", "too_difficult", "too_easy", "assumes_experience", "repetitive", "more_than_one",
      "confusing", "unrealistic", "other"] },
  ] },
  { key: "D", stage: "interview", questions: [
    { id: "d_hear", type: "choice", required: true, options: ["always", "usually", "sometimes", "rarely", "never"] },
    { id: "d_voice_natural", type: "rating", required: true },
    { id: "d_transcription", type: "choice", required: true, options: [
      "very_accurate", "mostly_accurate", "some_errors", "many_errors", "could_not_tell"] },
    { id: "d_mic_issue", type: "yesno", required: true },
    { id: "d_mic_types", type: "multi", required: true, showIf: is("d_mic_issue", "yes"), options: [
      "mic_did_not_start", "recording_stopped", "answer_cut_off", "poor_audio", "speech_not_detected", "other"] },
    { id: "d_other", type: "text", required: false },
  ] },
  { key: "E", stage: "feedback", questions: [
    { id: "e_consistent", type: "rating", required: true },
    { id: "e_understandable", type: "rating", required: true },
    { id: "e_useful", type: "rating", required: true },
    { id: "e_unfair", type: "yesno", required: true },
    { id: "e_unfair_explain", type: "text", required: true, showIf: is("e_unfair", "yes") },
    { id: "e_model_answers", type: "rating_na", required: true },
    { id: "e_english_feedback", type: "rating", required: true },
    { id: "e_mismatch", type: "yesno", required: true },
    { id: "e_mismatch_explain", type: "text", required: true, showIf: is("e_mismatch", "yes") },
  ] },
  { key: "F", stage: "technical", questions: [
    { id: "f_clear", type: "rating", required: true },
    { id: "f_lost", type: "yesno", required: true },
    { id: "f_lost_where", type: "text", required: true, showIf: is("f_lost", "yes") },
    { id: "f_unexpected", type: "yesno", required: true },
    { id: "f_unexpected_explain", type: "text", required: true, showIf: is("f_unexpected", "yes") },
    { id: "f_layout", type: "multi", required: false, options: [
      "text_cut_off", "overlapping", "off_screen", "did_not_fit", "mobile_layout", "spacing", "missing_image", "other"] },
  ] },
  { key: "G", stage: "technical", questions: [
    // The interface language is captured automatically and shown (not asked).
    { id: "g_language", type: "language", required: false },
    { id: "g_natural", type: "rating", required: true, showIf: nonEnglish },
    { id: "g_incorrect", type: "yesno", required: true, showIf: nonEnglish },
    { id: "g_untranslated", type: "yesno", required: true, showIf: nonEnglish },
    { id: "g_regional", type: "yesno", required: true, showIf: nonEnglish },
    { id: "g_where", type: "text", required: true, showIf: anyYes(["g_incorrect", "g_untranslated", "g_regional"]) },
    { id: "g_suggestion", type: "text", required: false, showIf: anyYes(["g_incorrect", "g_untranslated", "g_regional"]) },
  ] },
  { key: "H", stage: "technical", questions: [
    { id: "h_speed", type: "choice", required: true, options: ["very_fast", "fast", "acceptable", "slow", "very_slow"] },
    { id: "h_waits", type: "yesno", required: true },
    { id: "h_where", type: "multi", required: true, showIf: is("h_waits", "yes"), options: [
      "login", "preparation", "question_loading", "between_questions", "audio_generation", "answer_processing",
      "report_generation", "pdf_download", "other"] },
    { id: "h_crash", type: "yesno", required: true },
    { id: "h_crash_explain", type: "text", required: true, showIf: is("h_crash", "yes") },
  ] },
  { key: "I", stage: "technical", questions: [
    { id: "i_repractice", type: "yesno", required: true },
    { id: "i_repractice_clear", type: "rating", required: true, showIf: is("i_repractice", "yes") },
    { id: "i_repractice_worked", type: "yesno", required: true, showIf: is("i_repractice", "yes") },
    { id: "i_repractice_problem", type: "text", required: true,
      showIf: dep((a) => a.i_repractice === "yes" && a.i_repractice_worked === "no", ["i_repractice", "i_repractice_worked"]) },
    { id: "i_repractice_useful", type: "rating", required: true, showIf: is("i_repractice", "yes") },
    { id: "i_control", type: "choice", required: true, options: ["restarted", "ended", "neither"] },
    { id: "i_control_worked", type: "yesno", required: true, showIf: isIn("i_control", ["restarted", "ended"]) },
    { id: "i_control_problem", type: "text", required: true,
      showIf: dep((a) => ["restarted", "ended"].includes(a.i_control) && a.i_control_worked === "no", ["i_control", "i_control_worked"]) },
  ] },
  { key: "J", stage: "final", questions: [
    { id: "j_value", type: "rating", required: true },
    { id: "j_would_pay", type: "choice", required: true, options: ["yes", "maybe", "no"] },
    { id: "j_price", type: "choice", required: true, options: [
      "under_15", "15_19", "20_24", "25_29", "30_39", "40_plus", "would_not_pay"] },
  ] },
  { key: "K", stage: "final", questions: [
    { id: "k_liked", type: "text", required: true },
    { id: "k_improve", type: "text", required: true },
    { id: "k_expected", type: "text", required: false },
    { id: "k_other", type: "text", required: false },
    { id: "k_contact", type: "yesno", required: false },
    { id: "k_followup", type: "yesno", required: false },
  ] },
];

// Progress by stage (the brief: Experience → Interview → Feedback → Technical → Final Comments).
export const STAGES = ["experience", "interview", "feedback", "technical", "final"];

// The form with the admin's changes applied (2026-11-04):
//   deleted:   built-in question ids taken out (their follow-ups go with them);
//   added:     new questions, placed at the start, the end or after a question;
//   overrides: {id: {type, options}}: a built-in question's answers changed to another
//              type, with its own choices ("<id>__o1" …); its follow-ups go, since
//              they depended on the old answers;
//   order:     {section: [ids]}: the admin's order of a section's questions.
export const OVERRIDE_OPTION = (id, n) => `${id}__o${n}`;

function cutOff(structure) {
  return new Set([...(structure?.deleted || []), ...Object.keys(structure?.overrides || {})]);
}

export function buildSections(structure) {
  const deleted = new Set(structure?.deleted || []);
  const cut = cutOff(structure);
  const overrides = structure?.overrides || {};
  const gone = (q) => deleted.has(q.id) || (q.showIf?.deps || []).some((d) => cut.has(d));
  const added = (structure?.added || []).filter((a) => ADDED_ID_RE.test(a.id || "") && ADDED_TYPES.includes(a.type));
  return SECTIONS.map((section) => {
    let questions = section.questions.filter((q) => !gone(q)).map((q) => {
      const o = overrides[q.id];
      if (!o || !ADDED_TYPES.includes(o.type)) return q;
      const changed = { ...q, type: o.type, changedType: true };
      if (o.type === "choice" || o.type === "multi") changed.options = [...(o.options || [])];
      else delete changed.options;
      return changed;
    });
    for (const a of added.filter((x) => x.section === section.key)) {
      const q = { id: a.id, type: a.type, required: Boolean(a.required), added: true };
      if (a.type === "choice" || a.type === "multi") q.options = [...(a.options || [])];
      const at = a.after ? questions.findIndex((x) => x.id === a.after) : -1;
      if (a.after === "__start") questions.unshift(q);
      else if (at >= 0) questions.splice(at + 1, 0, q);
      else questions.push(q);
    }
    const order = (structure?.order || {})[section.key];
    if (Array.isArray(order) && order.length) {
      // Questions in the admin's order; any not in it keep their place after the one before.
      const rank = new Map(order.map((id, i) => [id, i]));
      let last = -1;
      const keyed = questions.map((q, i) => {
        if (rank.has(q.id)) last = rank.get(q.id);
        return { q, key: rank.has(q.id) ? rank.get(q.id) : last + 0.001 * (i + 1) };
      });
      questions = keyed.sort((x, y) => x.key - y.key).map((x) => x.q);
    }
    return { ...section, questions };
  });
}

// Built-in questions removed because a question they follow up on was deleted or
// had its answers changed.
export function removedWith(structure) {
  const deleted = new Set(structure?.deleted || []);
  const cut = cutOff(structure);
  const out = [];
  for (const section of SECTIONS) {
    for (const q of section.questions) {
      if (!deleted.has(q.id) && (q.showIf?.deps || []).some((d) => cut.has(d))) out.push(q.id);
    }
  }
  return out;
}

let current = SECTIONS;
export function setStructure(structure) {
  current = buildSections(structure);
}
export function formSections() {
  return current;
}

export function sectionsForStage(stage, sections = current) {
  return sections.filter((s) => s.stage === stage);
}

export function isShown(question, answers, ctx) {
  return !question.showIf || Boolean(question.showIf(answers, ctx));
}

function isAnswered(question, value) {
  if (question.type === "multi") return Array.isArray(value) && value.length > 0;
  if (question.type === "text") return typeof value === "string" && value.trim().length > 0;
  return value !== undefined && value !== null && value !== "";
}

export const isRating = (q) => q.type === "rating" || q.type === "rating_na";

// The ids of required questions that are shown but unanswered, in the given sections.
export function missingRequired(sections, answers, ctx) {
  const missing = [];
  for (const section of sections) {
    for (const q of section.questions) {
      if (q.required && q.type !== "language" && isShown(q, answers, ctx) && !isAnswered(q, answers[q.id])) {
        missing.push(q.id);
      }
      // Fair or Poor needs a comment (product owner, 2026-11-04).
      if (isRating(q) && isShown(q, answers, ctx) && LOW_RATINGS.includes(answers[q.id])
          && !String(answers[whyId(q.id)] || "").trim()) {
        missing.push(whyId(q.id));
      }
    }
  }
  return missing;
}

// Only the answers to questions that are shown (hidden follow-ups are dropped).
export function visibleAnswers(answers, ctx, sections = current) {
  const out = {};
  for (const section of sections) {
    for (const q of section.questions) {
      if (q.type !== "language" && isShown(q, answers, ctx) && isAnswered(q, answers[q.id])) {
        out[q.id] = q.type === "text" ? String(answers[q.id]).slice(0, TEXT_MAX) : answers[q.id];
        if (isRating(q) && LOW_RATINGS.includes(answers[q.id]) && String(answers[whyId(q.id)] || "").trim()) {
          out[whyId(q.id)] = String(answers[whyId(q.id)]).slice(0, TEXT_MAX);
        }
      }
    }
  }
  return out;
}
